import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { muezzinById } from "@/lib/prayerTimes";
import { logWarn } from "@/lib/log";

// Adhan playback (favourite muezzin, Fajr recording for Fajr) with a 6 s fade-in for automatic
// playback. `blocked` is set when the browser rejects autoplay so the UI can offer a tap-to-play.
// All handles are stable so the object can be shared through context.
export function useAdhanPlayer() {
  const [playing, setPlaying] = useState(null); // prayer key while playing
  const [blocked, setBlocked] = useState(null); // prayer key whose autoplay was rejected
  const audioRef = useRef(null);
  const fadeRef = useRef(null);

  const clearFade = () => { if (fadeRef.current) { clearInterval(fadeRef.current); fadeRef.current = null; } };
  const ensure = () => {
    if (!audioRef.current) {
      const a = new Audio();
      a.preload = "none";
      a.addEventListener("ended", () => setPlaying(null));
      audioRef.current = a;
    }
    return audioRef.current;
  };

  const play = useCallback(async (key, muezzinId, fadeIn = false) => {
    const a = ensure();
    clearFade();
    a.pause();
    const mz = muezzinById(muezzinId);
    a.src = key === "fajr" && mz.fajrSrc ? mz.fajrSrc : mz.src;
    a.currentTime = 0;
    a.volume = fadeIn ? 0 : 1;
    if (fadeIn) {
      const stepMs = 120, inc = stepMs / 6000;
      fadeRef.current = setInterval(() => { a.volume = Math.min(1, a.volume + inc); if (a.volume >= 1) clearFade(); }, stepMs);
    }
    try {
      await a.play();
      setPlaying(key); setBlocked(null);
      return true;
    } catch (e) {
      clearFade(); setPlaying(null); setBlocked(key);
      logWarn("adhan autoplay rejected:", e);
      return false;
    }
  }, []);
  const stop = useCallback(() => { clearFade(); const a = audioRef.current; if (a) { a.pause(); a.currentTime = 0; a.volume = 1; } setPlaying(null); setBlocked(null); }, []);

  useEffect(() => () => { clearFade(); if (audioRef.current) { audioRef.current.pause(); audioRef.current.src = ""; } }, []);
  return useMemo(() => ({ playing, blocked, play, stop }), [playing, blocked, play, stop]);
}
